import type { KnowledgeNodeDetail } from "@/api/memory";
import type { WorkSkillSection } from "@/api/work-skills";
import { translate, useT } from "@/i18n";
import { settingsSkillsMessages } from "@/i18n/messages/settings-skills";

export function textList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

export function sectionsFor(detail: KnowledgeNodeDetail): WorkSkillSection[] {
  if (detail.effective_steps) {
    return [
      {
        title: translate(settingsSkillsMessages, "yourSteps"),
        steps: detail.effective_steps,
      },
    ];
  }
  return Array.isArray(detail.metadata.sections)
    ? (detail.metadata.sections as WorkSkillSection[])
    : [];
}

export function DetailList({
  title,
  items,
}: {
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  return (
    <section className="space-y-1.5">
      <h4 className="text-appkit-caption font-semibold text-primary">
        {title}
      </h4>
      <ul className="list-disc space-y-1 pl-5 text-appkit-mini text-secondary">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

export function StepList({ sections }: { sections: WorkSkillSection[] }) {
  const t = useT(settingsSkillsMessages);
  return (
    <section className="space-y-3" data-testid="work-skill-steps">
      <h4 className="text-appkit-caption font-semibold text-primary">
        {t("steps")}
      </h4>
      {sections.map((section) => (
        <div key={section.title} className="space-y-2">
          {sections.length > 1 && (
            <h5 className="text-appkit-mini font-medium text-secondary">
              {section.title}
            </h5>
          )}
          <ol className="list-decimal space-y-2 pl-5 text-appkit-mini text-secondary">
            {section.steps.map((step) => (
              <li key={step.step_id}>
                <span className="font-medium text-primary">{step.text}</span>
                {textList(step.directions).length > 0 && (
                  <ul className="mt-1 list-disc space-y-1 pl-4">
                    {textList(step.directions).map((direction) => (
                      <li key={direction}>{direction}</li>
                    ))}
                  </ul>
                )}
                {textList(step.tips).map((tip) => (
                  <p key={tip} className="mt-1 text-muted">
                    {t("tip", { tip })}
                  </p>
                ))}
                {textList(step.tools).length > 0 && (
                  <p className="mt-1 text-muted">
                    {t("tools", { tools: textList(step.tools).join(", ") })}
                  </p>
                )}
                {textList(step.supplies).length > 0 && (
                  <p className="mt-1 text-muted">
                    {t("materials", {
                      materials: textList(step.supplies).join(", "),
                    })}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
}
